/* MenuOnboardingTour — 3-step swipeable coach mark that fires the very
 * first time an authenticated user lands on /dashboard after the
 * settings gear was replaced with the hamburger menu (iter99). Uses a
 * localStorage flag so it never re-fires. Purely cosmetic — no backend
 * or gameplay side-effects.
 *
 * Highlights the hamburger button by anchoring a spotlight ring to the
 * `[data-testid="btn-settings"]` element (the trigger kept the old
 * testid for API stability). Steps 2 & 3 add secondary spotlights to
 * the notifications / duel bells when present.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Menu, Bell, Swords } from 'lucide-react';

const STORAGE_KEY = 'wq_menu_tour_seen_v1';
export const REPLAY_TOUR_EVENT = 'wq:replay-menu-tour';

/** Fire from anywhere (menu item, help button) to restart the tour. */
export function replayMenuTour() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
  try { window.dispatchEvent(new CustomEvent(REPLAY_TOUR_EVENT)); } catch { /* ssr */ }
}

const STEPS = [
  {
    title: 'Your menu moved here',
    body: 'Tap the ☰ icon any time for Profile, Collections, Membership and every other page.',
    anchor: 'btn-settings',
    Icon: Menu,
  },
  {
    title: 'Search & WildCoins are in the menu',
    body: 'On phones, Search, your WildCoin balance and language switcher now live inside the menu — same buttons, just tucked away.',
    anchor: 'btn-settings',
    Icon: Menu,
  },
  {
    title: 'Alerts still live up top',
    body: 'Anything urgent — challenges, duels and notifications — still sits in the header so you never miss a beat.',
    anchor: 'btn-notifications',
    Icon: Bell,
  },
];

/** Read the pixel rect of the element with a given data-testid. The
 *  poller is completely inert when `enabled` is false so the tour has
 *  zero perf cost outside its brief on-screen lifetime. */
function useAnchorRect(testid, enabled) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!enabled || !testid) { setRect(null); return; }
    const update = () => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    const id = window.setInterval(update, 400);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [testid, enabled]);
  return rect;
}

export default function MenuOnboardingTour() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const activeStep = STEPS[step];
  const anchorRect = useAnchorRect(activeStep?.anchor, open);

  // Fire only on /dashboard visits, once ever per browser.
  useEffect(() => {
    if (location.pathname !== '/dashboard') return;
    let seen = false;
    try { seen = localStorage.getItem(STORAGE_KEY) === '1'; } catch { /* private mode */ }
    if (seen) return;
    // Give the header a beat to mount before we highlight it.
    const t = window.setTimeout(() => setOpen(true), 900);
    return () => window.clearTimeout(t);
  }, [location.pathname]);

  // Manual replay — dispatched by the "Show tips again" menu item.
  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener(REPLAY_TOUR_EVENT, handler);
    return () => window.removeEventListener(REPLAY_TOUR_EVENT, handler);
  }, []);

  const dismiss = (persist = true) => {
    setOpen(false);
    setStep(0);
    if (persist) {
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* silent */ }
    }
  };

  const next = () => {
    if (step >= STEPS.length - 1) return dismiss(true);
    setStep(s => s + 1);
  };
  const prev = () => setStep(s => Math.max(0, s - 1));

  // Swipe handling for the copy card
  const swipeHandlers = useMemo(() => {
    let startX = 0;
    return {
      onTouchStart: (e) => { startX = e.touches[0].clientX; },
      onTouchEnd: (e) => {
        const dx = (e.changedTouches[0].clientX || 0) - startX;
        if (dx < -40) next();
        else if (dx > 40) prev();
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (!open) return null;

  // Position the spotlight ring on top of the anchored element.
  const spotlight = anchorRect ? {
    top: anchorRect.top - 8,
    left: anchorRect.left - 8,
    width: anchorRect.width + 16,
    height: anchorRect.height + 16,
  } : null;

  return (
    <AnimatePresence>
      <motion.div
        key="tour-root"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[90]"
        data-testid="menu-onboarding-tour"
      >
        {/* Dim overlay — click to skip */}
        <button
          type="button"
          onClick={() => dismiss(true)}
          data-testid="menu-tour-backdrop"
          aria-label="Skip menu tour"
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        {/* Spotlight ring anchored to the header button */}
        {spotlight && (
          <motion.div
            key={`spot-${step}`}
            initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={spotlight}
            className="pointer-events-none absolute rounded-full ring-4 ring-emerald-300/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
            data-testid={`menu-tour-spotlight-${step}`}
          >
            <span className="absolute inset-0 rounded-full ring-2 ring-emerald-200 animate-ping opacity-70" />
          </motion.div>
        )}

        {/* Copy card — positioned near the top-right icons on the first two
            steps, then near the notification bell on step 3. Falls back to
            centered if we somehow can't measure the anchor. */}
        <motion.div
          key={`card-${step}`}
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          {...swipeHandlers}
          data-testid={`menu-tour-step-${step}`}
          style={spotlight ? { top: spotlight.top + spotlight.height + 14 } : undefined}
          className={`absolute left-4 right-4 mx-auto max-w-sm rounded-3xl bg-[#0f1a15]/95 border border-emerald-400/25 shadow-2xl shadow-emerald-500/10 p-5 ${
            spotlight ? '' : 'top-1/2 -translate-y-1/2'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
              <activeStep.Icon className="w-4 h-4 text-emerald-300" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">
                Quick tour · {step + 1} of {STEPS.length}
              </p>
              <p className="font-serif text-lg leading-snug mt-1">{activeStep.title}</p>
              <p className="text-sm text-white/80 mt-1.5 leading-relaxed">{activeStep.body}</p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(true)}
              data-testid="menu-tour-close"
              aria-label="Close tour"
              className="p-1.5 rounded-full hover:bg-white/5"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mt-4" data-testid="menu-tour-dots">
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStep(i)}
                data-testid={`menu-tour-dot-${i}`}
                aria-label={`Go to step ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-emerald-300' : 'w-1.5 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>

          {/* Nav buttons */}
          <div className="flex items-center justify-between mt-4 gap-3">
            <button
              type="button"
              onClick={() => dismiss(true)}
              data-testid="menu-tour-skip"
              className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-white"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  type="button"
                  onClick={prev}
                  data-testid="menu-tour-prev"
                  className="h-10 px-4 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest hover:bg-white/10 flex items-center gap-1.5"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <button
                type="button"
                onClick={next}
                data-testid="menu-tour-next"
                className="h-10 px-5 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-black text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 hover:brightness-110"
              >
                {step === STEPS.length - 1 ? 'Got it' : 'Next'}
                {step === STEPS.length - 1 ? null : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
